import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useIsAdmin, useMe } from '@/features/auth/hooks';
import { useCustomerDraft } from '@/features/customer/hooks';
import { useProductsStats } from '@/features/product/hooks';
import {
  getDashboard,
  getDashboardSection,
  type DashboardDto,
  type DashboardEntryDto,
  type DashboardSection,
} from './api';
import type { OnboardingState } from './onboarding';

const DASHBOARD_PAGE_SIZE = 5;

/**
 * Dashboard rollup query. Polls while anything is running so Running Now /
 * the in-flight KPI self-clear; goes quiet when the portfolio is idle.
 * Key shares the ['products'] prefix so every existing mutation
 * invalidation (dispatches, archives, uploads) refreshes it too.
 */
/**
 * The first-run onboarding gate, assembled ONCE for every consumer
 * (Sidebar rail, mobile nav drawer, dashboard page) so the lock states
 * can never disagree. Uses the lightweight /stats endpoint for "has any
 * product" and biases to UNLOCKED while the queries load, so an
 * already-set-up user never sees a flash of locked entries.
 */
export function useOnboardingState(): OnboardingState {
  const meQuery = useMe();
  const isAdmin = useIsAdmin();
  const customerQuery = useCustomerDraft();
  const statsQuery = useProductsStats();
  return {
    // Bias to true while loading: an already-accepted user never flashes the terms screen.
    termsAccepted: meQuery.isPending || meQuery.data?.terms_required === false,
    customerSubmitted:
      customerQuery.isPending || customerQuery.data?.status === 'submitted',
    hasProduct: statsQuery.isPending || (statsQuery.data?.total ?? 0) > 0,
    canEditCustomer: isAdmin,
    // Admin Simulators switch; only an explicit false greys the tab, so a
    // pending /me never flashes it locked.
    simulatorsEnabled: meQuery.data?.simulators_enabled !== false,
  };
}

export function useDashboard() {
  return useQuery<DashboardDto>({
    queryKey: ['products', 'dashboard'],
    queryFn: getDashboard,
    staleTime: 15 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return data.in_flight_runs > 0 ? 10_000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

export interface DashboardSectionResult {
  items: DashboardEntryDto[];
  hasMore: boolean;
  isFetchingMore: boolean;
  loadMore: () => void;
}

function entryKey(e: DashboardEntryDto): string {
  return `${e.agent}-${e.run_id}-${e.country ?? ''}`;
}

/** Stable de-dup: a live poll of the first page can momentarily overlap an
 *  already-loaded extra page; keep the first occurrence of each run. */
function dedupEntries(entries: DashboardEntryDto[]): DashboardEntryDto[] {
  const seen = new Set<string>();
  const out: DashboardEntryDto[] = [];
  for (const e of entries) {
    const k = entryKey(e);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(e);
    }
  }
  return out;
}

/**
 * On-demand "load more" for ONE dashboard list section.
 *
 * COLLAPSED, it shows the live-polling first page from the /api/dashboard
 * bundle. On the first "Load more" it SNAPSHOTS that first page + cursor and
 * pages on from the snapshot via /api/dashboard/{section} — so the expanded
 * view is an internally-consistent keyset slice that does NOT shift as the
 * bundle's first page keeps re-polling. (Concatenating the live first page
 * instead would drop/dupe a row at the page boundary when a new run lands.)
 * Extra pages are fetched only on click, never polled: one request (hence one
 * DB connection) per load/poll, plus one per Load-more click.
 */
export function useDashboardSectionLoadMore(
  section: DashboardSection,
  firstPage: DashboardEntryDto[] | undefined,
  firstPageCursor: string | null | undefined,
  { enabled }: { enabled: boolean },
): DashboardSectionResult {
  // Frozen at first expand: this section's own first page + the cursor to page
  // on from. null while collapsed.
  const [seed, setSeed] = useState<
    { page: DashboardEntryDto[]; cursor: string | null } | null
  >(null);
  const query = useInfiniteQuery({
    queryKey: ['products', 'dashboard', 'section', section],
    queryFn: ({ pageParam }) =>
      getDashboardSection(section, pageParam, DASHBOARD_PAGE_SIZE),
    initialPageParam: (seed?.cursor ?? null) as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: enabled && seed !== null,
    staleTime: 15 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  const extra = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );
  const items = useMemo(
    () => (seed ? dedupEntries([...seed.page, ...extra]) : (firstPage ?? [])),
    [seed, extra, firstPage],
  );
  // Keep the footer up while a page is in flight so it doesn't flicker out on
  // the first click (hasNextPage is undefined until that fetch lands).
  const hasMore = seed
    ? query.isFetching || (query.hasNextPage ?? false)
    : (firstPageCursor ?? null) != null;
  const loadMore = () => {
    if (!seed) {
      // First click freezes the current first page + cursor, then pages on.
      setSeed({ page: firstPage ?? [], cursor: firstPageCursor ?? null });
      return;
    }
    if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
  };
  return {
    items,
    hasMore,
    isFetchingMore: enabled && seed !== null && query.isFetching,
    loadMore,
  };
}
